/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from '@/common';
import { Search } from '@/components/common/Search';
import { Label, LABEL_TYPE } from '@/components/generic';
import { AssetType } from '@/enums';
import { Asset, SelectOptionProps } from '@/types';

import { AssetItem } from '../Event/Asset/AssetItem';
import { AssetTypeFilter } from '../Event/CeremonyTemplate/AssetTypeFilter';

interface AssetPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  handleSubmit: () => void;
  searchCallback: (query: string) => void;
  handleFilterClick: (value: SelectOptionProps) => void;
  organizationAssetsData: any;
  selectedAssets: Asset[];
  handleClickAsset: (asset: Asset, event: React.MouseEvent, index: number) => void;
  inViewRef: (node?: Element | null) => void;
}

export const AssetPickerModal = ({
  isOpen,
  onClose,
  handleSubmit,
  searchCallback,
  handleFilterClick,
  organizationAssetsData,
  selectedAssets,
  handleClickAsset,
  inViewRef
}: AssetPickerModalProps) => {
  const [t] = useTranslation('common');
  const simpleAssetFilterOptions: { value: string; label: string }[] = useMemo(
    () => [
      { value: String(AssetType.IMAGE), label: t('image', { ns: 'common' }) },
      { value: String(AssetType.VIDEO), label: t('video', { ns: 'common' }) },
      { value: String(AssetType.AUDIO), label: t('audio', { ns: 'common' }) }
    ],
    [t]
  );
  const [activeFilter, setActiveFilter] = useState<SelectOptionProps | null>(null);

  const handleActiveFilterClick = (value: SelectOptionProps) => {
    if (activeFilter?.value === value?.value) {
      setActiveFilter(null);
    } else {
      setActiveFilter(value);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      modalClasses='w-full h-full'
      height='h-full'
      hasButton={true}
      buttonText={t('save')}
      buttonClasses={'flex justify-end'}
      handleSubmit={handleSubmit}
      isButtonDisabled={selectedAssets.length === 0}
      label={t('selectAsset')}>
      <div className='flex size-full flex-col rounded-lg bg-color-light-blue'>
        <div className='flex w-full justify-between px-6 py-3'>
          <Label text={t('uploadedContent')} type={LABEL_TYPE.H2} />
        </div>
        <div className='w-full border-b border-secondary-blue-gray'></div>
        <div className='flex w-full px-6 pt-2'>
          <Search callback={searchCallback} />
        </div>
        <div className='grid w-full grid-cols-2 gap-4 px-6 py-5'>
          {simpleAssetFilterOptions.map((item) => (
            <AssetTypeFilter
              title={item.label}
              key={item.value}
              onClick={() => {
                handleFilterClick(item);
                handleActiveFilterClick(item);
              }}
              isClicked={item.value === activeFilter?.value}
            />
          ))}
        </div>
        <div className='no-scrollbar mb-5 grid grid-cols-3 gap-6 overflow-y-auto px-6'>
          {organizationAssetsData?.pages.map((page: any, pageIndex: number) => {
            const offset = organizationAssetsData.pages
              .slice(0, pageIndex)
              .reduce((acc: number, p: any) => acc + p.data?.items.length, 0);

            return page.data?.items.map((asset: Asset, index: number) => {
              const globalIndex = offset + index;
              return (
                <div
                  key={asset.id}
                  className='cursor-pointer'
                  onClick={(e) => handleClickAsset(asset, e, globalIndex)}>
                  <AssetItem
                    asset={asset}
                    assetType={asset.assetType}
                    canBeDeleted={false}
                    selectedVideoUrl={''}
                    multiselect={true}
                    selectedAssets={selectedAssets}
                  />
                </div>
              );
            });
          })}
          <div ref={inViewRef}></div>
        </div>
      </div>
    </Modal>
  );
};
